import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  SxProps,
  Theme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DynamicFormProps,
  DynamicFormField,
} from "@/models/dynamic-form.model";
import { InputFieldType } from "@/constants/enums/input-field-type.enum";
import moment from "moment";
import dateHandler from "@/composables/dateUtil";
import FormField from "./DynamicForm/FormField";

interface CommonInputProps {
  name: string;
  value: any;
  onChange: (e: React.ChangeEvent<any>) => void;
  onBlur: (e: React.FocusEvent<any>) => void;
  error: boolean;
  helperText: string | false;
  placeholder?: string;
}

interface ValidationSchema {
  [key: string]: Yup.Schema;
}

interface FormValues {
  [key: string]: any;
}

const datePickerSx: SxProps<Theme> = {
  backgroundColor: "white",
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#E9EAEB" },
    "&:hover fieldset": { borderColor: "#146C94" },
    "&.Mui-focused fieldset": { borderColor: "#146C94" },
  },
};

const DynamicForm = memo<DynamicFormProps>(
  ({
    fields,
    initialValues = {},
    onSubmit,
    submitButtonText = "Save",
    readOnly = false,
    disabled = false,
  }) => {
    const { toStandardDisplayDate } = dateHandler();
    const [localFields, setLocalFields] = useState<DynamicFormField[]>(fields);
    const [isLoading, setIsLoading] = useState(false);

    // Generate validation schema
    const generateValidationSchema = (fields: DynamicFormField[]) => {
      return Yup.object().shape(
        fields.reduce<ValidationSchema>((acc, field) => {
          let validator = field.validation || Yup.string();
          if (field.type === InputFieldType.ARRAY_OF_OBJECT) {
            validator = generateValidationSchema(field.children || []);
          }

          if (field.required) {
            validator = validator.required(`${field.label} is required`);
          }

          return { ...acc, [field.name]: validator };
        }, {}),
      );
    };

    const validationSchema = useMemo(() => {
      return generateValidationSchema(localFields);
    }, [localFields]);

    const initialValueOfField = (field: DynamicFormField): any => {
      if (field.type === InputFieldType.DATE && initialValues?.[field.name]) {
        return toStandardDisplayDate(initialValues[field.name]);
      }
      return initialValues[field.name];
    };

    // Generate initial values
    const defaultValues = fields.reduce<FormValues>(
      (acc, field) => ({
        ...acc,
        [field.name]:
          initialValueOfField(field) ??
          field.defaultValue ??
          (field.type === InputFieldType.CHECKBOX
            ? false
            : field.type === InputFieldType.NUMBER
              ? 0
              : ""),
      }),
      {},
    );

    const getDatePayload = (date: string) => {
      let format = "YYYY-MM-DD";
      if (date.match(/^\d{2}\-\d{2}\-\d{4}$/)) {
        format = "DD-MM-YYYY";
      } else if (date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        format = "DD/MM/YYYY";
      } else if (date.match(/^\d{4}\-\d{2}\-\d{2}$/)) {
        format = "YYYY-MM-DD";
      } else if (date.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
        format = "YYYY/MM/DD";
      }
      const formatedDate = moment(date, format).format("YYYY-MM-DD");
      return formatedDate;
    };

    const formik = useFormik({
      initialValues: defaultValues,
      validationSchema,
      onSubmit: async (
        values: FormValues,
        helpers: FormikHelpers<FormValues>,
      ) => {
        setIsLoading(true);
        try {
          const data = Object.keys(values).reduce((acc: any, key: string) => {
            const field = fields.find((f) => f.name === key);
            if (field) {
              acc[field.name] =
                field.type === InputFieldType.DATE
                  ? getDatePayload(values[key])
                  : values[key];
            }
            return acc;
          }, {});
          await onSubmit(data);
        } catch (error) {
          console.error("Form submission error:", error);
          helpers.setSubmitting(false);
        } finally {
          setIsLoading(false);
        }
      },
    });

    const getNewNestedFieldObjects = (oldField: DynamicFormField) => {
      const newField = { ...oldField };
      const lastChild = newField.children?.slice?.(-1)?.[0];
      if (lastChild) {
        newField.children?.push({
          ...lastChild,
          name: `${lastChild.name.split("_")?.[0]}_${newField.children?.length + 1}`,
          label: `${lastChild.label.split(" ")?.[0]} ${newField.children?.length + 1}`,
        });
      }
      return newField;
    };

    useEffect(() => {
      formik.setValues(initialValues);
      const oldFields = [...fields];
      oldFields.forEach((field, index) => {
        if (field.type === InputFieldType.ARRAY_OF_OBJECT) {
          const newData = initialValues[field.name];
          if (newData) {
            const newArray = Object.keys(newData);
            newArray.forEach?.((item: any) => {
              if (newArray.length > (oldFields[index].children?.length || 0)) {
                const newField = getNewNestedFieldObjects(oldFields[index]);
                oldFields[index] = newField;
              }
            });
          }
        }
      });
    }, [initialValues]);

    useEffect(() => {
      setLocalFields(fields);
    }, [fields]);

    // Function to check if a dependent field should be shown
    const shouldShowField = useCallback(
      (field: DynamicFormField) => {
        if (!field.dependsOn) return true;

        const { field: dependentField, value } = field.dependsOn;
        const dependentValue = formik.values[dependentField];

        return dependentValue === value;
      },
      [formik.values],
    );

    return (
      <div className="tw-bg-[#ffffff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#E9EAEB] tw-w-full">
        <div className="tw-px-6 tw-py-4">
          <span className="tw-text-[#232323] tw-text-base tw-font-semibold">
            Inputs
          </span>
        </div>
        <hr className="tw-border-[#E9EAEB]" />
        <div className="tw-px-6 tw-py-4 tw-flex tw-flex-col tw-gap-4 tw-max-h-[900px] tw-overflow-y-auto">
          {fields.map(
            (field) =>
              // Only render field if its dependency condition is met
              shouldShowField(field) && (
                <div key={field.name} className="tw-flex tw-flex-col tw-gap-2">
                  {field.type !== InputFieldType.ARRAY_OF_OBJECT && (
                    <label className="tw-text-[#414651] tw-text-sm tw-font-medium">
                      {field.label}
                      {!readOnly &&
                        field.required &&
                        field.type !== InputFieldType.CHECKBOX && (
                          <span className="tw-text-red-500">*</span>
                        )}
                    </label>
                  )}
                  <FormField
                    field={field}
                    formik={formik}
                    readOnly={readOnly}
                    datePickerSx={datePickerSx}
                  />
                  {field.type !== InputFieldType.ARRAY_OF_OBJECT &&
                    !readOnly &&
                    formik.touched[field.name] &&
                    formik.errors[field.name] && (
                      <div className="tw-text-red-500 tw-text-xs">
                        {formik.errors[field.name] as string}
                      </div>
                    )}
                </div>
              ),
          )}
          {!readOnly && (
            <button
              type="button"
              className={`tw-w-full tw-mt-4 tw-bg-[#146C94] tw-text-white tw-py-2 tw-px-4 tw-rounded-lg 
                ${
                  !formik.isValid || isLoading
                    ? "tw-opacity-50 tw-cursor-not-allowed"
                    : "hover:tw-bg-[#105C84]"
                } 
                tw-transition-colors tw-duration-200`}
              onClick={() => formik.handleSubmit()}
              disabled={!formik.isValid || isLoading}
            >
              {isLoading ? "Saving..." : submitButtonText}
            </button>
          )}
        </div>
      </div>
    );
  },
);

DynamicForm.displayName = "DynamicForm";

export default DynamicForm;
